// frontend/src/pages/asset-admin/AssetAdminTransfers.js
/**
 * Asset Admin - Transactions (Movement History) Page
 *
 * View all asset movements with filtering and search.
 * UI: "Transactions" = backend AssetTransfer audit trail (assignments, transfers, returns, repairs, disposals, etc.)
 * Theme: Red (Admin)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Pagination } from '../../components/UI/Pagination';
import { FilterButton } from '../../components/UI/FilterButton';
import {
  ArrowLeftRight, Package,
  AlertCircle, Laptop, Car, FileKey,
  DoorOpen, CreditCard, Fuel, Smartphone, Grid3X3, List, ArrowRight,
  CheckCircle, XCircle, X, Search,
  RotateCcw, Truck, Wrench, Trash2, HelpCircle, UserMinus, ShieldOff,
} from 'lucide-react';
import { useTabPersistence, usePersistence } from '../../hooks/usePersistence';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import PillToggle from '../../components/UI/PillToggle';
import { useAssetTransfers, useCancelTransfer } from '../../hooks/useAssetInventoryData';
import { useAuth } from '../../contexts/AuthContext';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { TransferTypeBadge } from '../../components/UI/TransferTypeBadge';
import ConfirmModal from '../../components/UI/ConfirmModal';
import TransferDetailModal from '../../components/TransferDetailModal';
import TransferActionsCell, { hasTransferSecondaryActions } from './TransferActionsCell';
import LetterPreviewModal from '../../components/LetterPreviewModal';
import { formatDate } from '../../utils/dateUtils';
import { formatAssetTagForDisplay } from '../../utils/mobileLineDisplay';
import { getTransferFromParty, getTransferToParty } from '../../utils/transferDisplayUtils';

const PAGE_SIZE = 25;

// ============================================
// CONSTANTS
// ============================================
const VALID_TABS = ['all', 'pending_acceptance', 'pending_receipt', 'completed', 'rejected', 'cancelled'];

// Transfer types exposed by the backend filterset_fields = ['transfer_type']
// Multi-select sends transfer_type__in=a,b — backend must support __in lookup.
const TRANSFER_TYPE_OPTIONS = [
  { value: 'initial',       label: 'Assignment',           icon: CheckCircle },
  { value: 'transfer',      label: 'Transfer',             icon: ArrowLeftRight },
  { value: 'deployment',    label: 'Deployment',           icon: Truck },
  { value: 'return',        label: 'Return to Inventory',  icon: RotateCcw },
  { value: 'offboarding',   label: 'Offboarding',          icon: UserMinus },
  { value: 'access_deactivation', label: 'Access Deactivated', icon: ShieldOff },
  { value: 'repair',        label: 'Repair',               icon: Wrench },
  { value: 'repair_return', label: 'Returned from Workshop', icon: Wrench },
  { value: 'disposal',      label: 'Disposal',             icon: Trash2 },
  { value: 'lost',          label: 'Lost / Missing',       icon: HelpCircle },
  { value: 'found',         label: 'Found',                icon: Package },
];

// ============================================
// FILTER CHECKBOX (scoped to this page)
// ============================================
const FilterCheckbox = ({ label, checked, onChange, icon: Icon }) => (
  <label className="flex items-center gap-2 cursor-pointer group py-1.5">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500 focus-visible:ring-offset-0 cursor-pointer transition-colors"
    />
    {Icon && <Icon className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />}
    <span className="text-sm text-gray-700 group-hover:text-gray-900 select-none">{label}</span>
  </label>
);

// ============================================
// CATEGORY ICON (aligned with AssetAdminAssets.js)
// ============================================
const CategoryIcon = ({ code, className = "w-4 h-4" }) => {
  const icons = {
    IT: Laptop,
    ACC: DoorOpen,
    FLT: Car,
    LIC: FileKey,
    OFF: Package,
    FUEL: CreditCard,
    DSL: Fuel,
    TEL: Smartphone,
  };
  const Icon = icons[code] || Package;
  return <Icon className={className} />;
};

// Category color mapping (aligned with AssetAdminAssets.js)
const getCategoryColors = (code) => {
  const colors = {
    IT: { bg: 'bg-blue-100', text: 'text-blue-600' },
    ACC: { bg: 'bg-purple-100', text: 'text-purple-600' },
    FLT: { bg: 'bg-green-100', text: 'text-green-600' },
    LIC: { bg: 'bg-amber-100', text: 'text-amber-600' },
    OFF: { bg: 'bg-gray-100', text: 'text-gray-600' },
    FUEL: { bg: 'bg-orange-100', text: 'text-orange-600' },
    DSL: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
    TEL: { bg: 'bg-sky-100', text: 'text-sky-600' },
  };
  return colors[code] || { bg: 'bg-gray-100', text: 'text-gray-600' };
};

// ============================================
// VIEW MODE HELPERS
// ============================================
const getDefaultViewMode = () => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('asset-admin-transfers-viewMode');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed === 'grid' || parsed === 'list') return parsed;
      } catch (e) { /* use default */ }
    }
    return window.innerWidth < 768 ? 'grid' : 'list';
  }
  return 'grid';
};

// ============================================
// TRANSFER CARD (grid + list-mobile)
// Ops scan order: asset → status → type/date → who→who (never truncated) → actions.
// Reference is audit metadata — demoted below the route, not a primary row.
// ============================================
const TransferCard = ({ transfer, onView, onViewLetter, onCancelClick, isCancelling, currentUserStaffId, isAssetCustodian }) => {
  const categoryCode = transfer.asset_detail?.asset_type?.category?.code || transfer.category_code;
  const colors = getCategoryColors(categoryCode);
  const fromParty = getTransferFromParty(transfer);
  const toParty = getTransferToParty(transfer);

  const openDetails = () => onView?.(transfer);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openDetails}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDetails();
        }
      }}
      aria-label={`View details for ${formatAssetTagForDisplay(transfer.asset_tag) || 'transfer'}${transfer.transfer_reference ? ` (${transfer.transfer_reference})` : ''}`}
      className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col h-full gap-3 text-left hover:shadow-md hover:border-gray-300 transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
    >
      <div className="flex-1">
      {/* Header — asset identity + status */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-10 h-10 ${colors.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
            <CategoryIcon code={categoryCode} className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 truncate whitespace-nowrap">{formatAssetTagForDisplay(transfer.asset_tag)}</h4>
            <p className="text-xs text-gray-500 truncate">{transfer.asset_type_name}</p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={transfer.status} />
        </div>
      </div>

      {/* Meta — type + date share one row */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <TransferTypeBadge type={transfer.transfer_type} />
        <span className="inline-flex items-center text-xs text-gray-500">
          {formatDate(transfer.effective_date || transfer.transfer_date)}
        </span>
      </div>

      {/* Route — single custody path (matches table / dashboard scan pattern) */}
      <div
        className="mt-3 rounded-lg bg-gray-50 border border-gray-100 px-2.5 py-2"
        aria-label={`From ${fromParty} to ${toParty}`}
      >
        <p className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm leading-snug">
          <span className="text-gray-600 break-words">{fromParty}</span>
          <ArrowRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 self-center" aria-hidden="true" />
          <span className="font-medium text-gray-900 break-words">{toParty}</span>
        </p>
        {transfer.to_project_code && (
          <p className="text-xs text-indigo-600 font-mono mt-0.5">{transfer.to_project_code}</p>
        )}
      </div>

      {/* Reference — tertiary audit id */}
      {transfer.transfer_reference && (
        <p
          className="mt-2 text-[11px] text-gray-400 font-mono truncate"
          title={transfer.transfer_reference}
        >
          {transfer.transfer_reference}
        </p>
      )}
      </div>

      {/* Footer — Letter/Cancel only; card tap is the View affordance */}
      {hasTransferSecondaryActions(transfer, currentUserStaffId, isAssetCustodian, onViewLetter) && (
        <div
          className="card-action-footer"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TransferActionsCell
            transfer={transfer}
            currentUserStaffId={currentUserStaffId}
            isAssetCustodian={isAssetCustodian}
            onView={onView}
            onViewLetter={onViewLetter}
            onCancelClick={onCancelClick}
            isCancelling={isCancelling}
            showView={false}
          />
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AssetAdminTransfers = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useTabPersistence('asset-admin-transfers', 'all', VALID_TABS);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = usePersistence('asset-admin-transfers-viewMode', getDefaultViewMode());
  const [cancellingTransferId, setCancellingTransferId] = useState(null);
  const [transferToCancel, setTransferToCancel] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [viewingTransfer, setViewingTransfer] = useState(null);
  const [letterModal, setLetterModal] = useState({ open: false, transfer: null });

  const handleViewLetter = (transfer) => {
    if (!transfer?.ack_letter_id) return;
    setLetterModal({ open: true, transfer });
  };

  // Filter panel state
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [typeFilters, setTypeFilters] = useState([]);

  const activeFilterCount = typeFilters.length;
  const hasActiveFilters = activeFilterCount > 0;

  const toggleTypeFilter = (value) => {
    setTypeFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setTypeFilters([]);
    setCurrentPage(1);
  };

  const queryParams = useMemo(() => {
    const p = { page: currentPage, page_size: PAGE_SIZE };
    if (activeTab !== 'all') p.status = activeTab;
    if (searchQuery.trim()) p.search = searchQuery.trim();
    // transfer_type__in requires backend FilterSet support for __in lookup.
    // Falls back to exact match when only one type is selected.
    if (typeFilters.length === 1) {
      p.transfer_type = typeFilters[0];
    } else if (typeFilters.length > 1) {
      p.transfer_type__in = typeFilters.join(',');
    }
    return p;
  }, [currentPage, activeTab, searchQuery, typeFilters]);

  const { data, isLoading, error } = useAssetTransfers(queryParams);
  const cancelTransfer = useCancelTransfer();

  const transfers = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleConfirmCancel = () => {
    if (!transferToCancel) return;
    cancelTransfer.mutate(
      { transferId: transferToCancel.id, reason: cancellationReason },
      {
        onMutate: () => setCancellingTransferId(transferToCancel.id),
        onSettled: () => {
          setCancellingTransferId(null);
          setTransferToCancel(null);
          setCancellationReason('');
        },
      }
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, typeFilters]);
  
  // Tab definitions
  const tabs = [
    { id: 'all', name: 'All', icon: ArrowLeftRight },
    { id: 'pending_acceptance', name: 'Pending', icon: AlertCircle },
    { id: 'pending_receipt', name: 'Pending Receipt', icon: Package },
    { id: 'completed', name: 'Completed', icon: CheckCircle },
    { id: 'rejected', name: 'Rejected', icon: XCircle },
    { id: 'cancelled', name: 'Cancelled', icon: X },
  ];

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ArrowLeftRight className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Transaction History</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">View all asset movements</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Search + Filter + View Toggle */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="p-4">
          <div className="flex items-center justify-between gap-3">
            {/* Left: Search */}
            <DebouncedSearchInput
              value={searchQuery}
              onSearch={setSearchQuery}
              placeholder="Search by person, asset, reference, project, or workshop"
              theme="admin"
              className="max-w-xl"
            />

            {/* Right: View → Filter */}
            <div className="flex items-center gap-2">
              <PillToggle
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { id: 'grid', label: 'Grid view', icon: Grid3X3 },
                  { id: 'list', label: 'List view', icon: List },
                ]}
                variant="icon"
                theme="admin"
                ariaLabel="View mode"
              />
              <FilterButton
                theme="admin"
                isOpen={showFilterPanel}
                activeCount={activeFilterCount}
                onClick={() => setShowFilterPanel(v => !v)}
              />
            </div>
          </div>
        </div>

        {/* Collapsible Filter Panel */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
          showFilterPanel ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-4 pb-4 pt-2 border-t border-gray-100">
            {hasActiveFilters && (
              <div className="flex justify-end mb-2">
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              </div>
            )}
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <ArrowLeftRight className="w-3.5 h-3.5" />
              Transfer Type
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4">
              {TRANSFER_TYPE_OPTIONS.map((opt) => (
                <FilterCheckbox
                  key={opt.value}
                  label={opt.label}
                  icon={opt.icon}
                  checked={typeFilters.includes(opt.value)}
                  onChange={() => toggleTypeFilter(opt.value)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <div className="px-4 pb-3 border-t border-gray-100 pt-2 flex items-center flex-wrap gap-2">
            <span className="text-xs font-medium text-gray-500">Active:</span>
            {typeFilters.map((value) => {
              const opt = TRANSFER_TYPE_OPTIONS.find((o) => o.value === value);
              return (
                <span
                  key={value}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full"
                >
                  {opt?.label || value}
                  <button
                    onClick={() => toggleTypeFilter(value)}
                    className="hover:bg-red-100 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
            <button
              onClick={clearAllFilters}
              className="text-xs text-gray-500 hover:text-red-600 font-medium ml-1"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Transfers Content */}
      <div className={`${viewMode === 'list' ? 'bg-white rounded-2xl shadow-sm border overflow-hidden' : ''}`}>
        {isLoading ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-xl border p-4 animate-pulse flex flex-col h-full gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-gray-200 rounded w-24"></div>
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  </div>
                  <div className="mt-3 ml-[52px] space-y-2 flex-1">
                    <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                    <div className="h-3 bg-gray-200 rounded w-36"></div>
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                  <div className="card-action-footer">
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="animate-pulse">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="grid grid-cols-7 gap-4">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="px-6 py-4">
                    <div className="grid grid-cols-7 gap-4 items-center">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                      <div className="h-4 bg-gray-200 rounded w-28"></div>
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                      <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : error ? (
          <div className={`text-center py-12 ${viewMode === 'grid' ? 'bg-white rounded-2xl shadow-sm border' : ''}`}>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-gray-500">Failed to load transactions</p>
          </div>
        ) : transfers.length === 0 ? (
          <div className={`text-center py-12 ${viewMode === 'grid' ? 'bg-white rounded-2xl shadow-sm border' : ''}`}>
            {searchQuery.trim() ? (
              <>
                <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No transactions found</h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto">
                  No transactions match &ldquo;{searchQuery.trim()}&rdquo;.
                  Try a name, staff ID, asset tag, serial, project, or workshop.
                </p>
              </>
            ) : (
              <>
                <ArrowLeftRight className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No transactions found</h3>
                <p className="text-sm text-gray-500">Asset movements will appear here.</p>
              </>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {transfers.map(transfer => (
              <TransferCard
                key={transfer.id}
                transfer={transfer}
                currentUserStaffId={user?.staff_id}
                isAssetCustodian={user?.is_asset_custodian ?? true}
                onView={(t) => setViewingTransfer(t)}
                onViewLetter={handleViewLetter}
                onCancelClick={(t) => setTransferToCancel(t)}
                isCancelling={cancellingTransferId === transfer.id}
              />
            ))}
          </div>
        ) : (
          <>
            {/* Mobile — same TransferCard as grid */}
            <div className="md:hidden space-y-3 p-4">
              {transfers.map((transfer) => (
                <TransferCard
                  key={transfer.id}
                  transfer={transfer}
                  currentUserStaffId={user?.staff_id}
                  isAssetCustodian={user?.is_asset_custodian ?? true}
                  onView={(t) => setViewingTransfer(t)}
                  onViewLetter={handleViewLetter}
                  onCancelClick={(t) => setTransferToCancel(t)}
                  isCancelling={cancellingTransferId === transfer.id}
                />
              ))}
            </div>
            {/* Desktop table (md and up) */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="table-header border-b">
                <tr>
                  <th className="table-header-cell">Reference</th>
                  <th className="table-header-cell">Asset</th>
                  <th className="table-header-cell">Type</th>
                  <th className="table-header-cell">From → To</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell text-right">Date</th>
                  <th className="table-header-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transfers.map(transfer => (
                  <tr key={transfer.id} className="table-row">
                    <td className="px-4 py-3">
                      <p className="text-sm font-mono text-gray-600">{transfer.transfer_reference}</p>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const categoryCode = transfer.asset_detail?.asset_type?.category?.code || transfer.category_code;
                        const colors = getCategoryColors(categoryCode);
                        return (
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 ${colors.bg} rounded-full flex items-center justify-center`}>
                              <CategoryIcon code={categoryCode} className={`w-4 h-4 ${colors.text}`} />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{formatAssetTagForDisplay(transfer.asset_tag)}</p>
                              <p className="text-xs text-gray-500">{transfer.asset_type_name}</p>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <TransferTypeBadge type={transfer.transfer_type} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600">{getTransferFromParty(transfer)}</span>
                        <span className="text-gray-400">→</span>
                        <span className="text-gray-900 font-medium">
                          {getTransferToParty(transfer)}
                        </span>
                        {transfer.to_project_code && (
                          <span className="text-xs text-indigo-600 font-mono">{transfer.to_project_code}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={transfer.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm text-gray-600">
                        {formatDate(transfer.effective_date || transfer.transfer_date)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <TransferActionsCell
                        transfer={transfer}
                        currentUserStaffId={user?.staff_id}
                        isAssetCustodian={user?.is_asset_custodian ?? true}
                        onView={(t) => setViewingTransfer(t)}
                        onViewLetter={handleViewLetter}
                        onCancelClick={(t) => setTransferToCancel(t)}
                        isCancelling={cancellingTransferId === transfer.id}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          theme="admin"
        />
      </div>

      <ConfirmModal
        isOpen={!!transferToCancel}
        title="Cancel assignment"
        confirmLabel="Cancel assignment"
        variant="danger"
        onConfirm={handleConfirmCancel}
        onCancel={() => { setTransferToCancel(null); setCancellationReason(''); }}
        isLoading={cancelTransfer.isPending}
        theme="admin"
      >
        <p className="text-sm text-gray-700">
          Cancel this assignment? The asset will return to available.
        </p>
        <div className="mt-3">
          <label htmlFor="cancel-assignment-reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason for cancellation
          </label>
          <textarea
            id="cancel-assignment-reason"
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="Enter reason for cancellation..."
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
          />
        </div>
      </ConfirmModal>

      <TransferDetailModal
        isOpen={!!viewingTransfer}
        transfer={viewingTransfer}
        onClose={() => setViewingTransfer(null)}
        theme="admin"
        canAddPhotos
      />

      {letterModal.open && letterModal.transfer?.ack_letter_id && (
        <LetterPreviewModal
          letterId={letterModal.transfer.ack_letter_id}
          letterReference={letterModal.transfer.ack_letter_reference}
          assetTag={letterModal.transfer.asset_tag}
          onClose={() => setLetterModal({ open: false, transfer: null })}
          theme="admin"
          allLettersHref={null}
        />
      )}
    </div>
  );
};

export default AssetAdminTransfers;
